/**
 * Notification preference metadata — shared by the profile settings sections
 * (global prefs live in the Notifications section, gym-scoped prefs in the
 * My gyms per-gym hub).
 *
 * Labels come from i18n `settings.notif.pref.<key>`, group headers from
 * `settings.notif.group.<group>`. Keep the keys aligned with the prefKeys
 * used by the notification Cloud Functions.
 */
import { toMillis } from "./eventUtils"; // shared Firestore-timestamp coercion

export const NOTIF_PREF_GROUPS = [
  { group: "community", icon: "👥", keys: ["friendRequests", "watchedRoute", "gymNewRoutes", "gymNewComp", "eventWaitlist", "eventReminder", "sprayProblemComments"] },
  { group: "gymAdmin", icon: "🏛️", keys: ["gymRouteFreshness", "mauBand", "gymNewMember"] },
  { group: "platform", icon: "🛡️", keys: ["newGym", "platformMauBand", "crashSpike", "nameReview"] },
  // Spraywall moderation: held by the spraywall.moderate capability, not by a role.
  // Its only key is gym-scoped, so the group never renders in the global Notifications section.
  { group: "moderation", icon: "🚩", keys: ["sprayReports"] },
];

/**
 * The personal Spraywall pref (a comment on the user's own route; a star rating
 * sends no push, so there is no like / rating pref). It controls nothing for a
 * user without a Spraywall gym, so the Notifications
 * section only shows them when some gym of the user has the add-on active (D86).
 */
export const SPRAY_PERSONAL_PREF_KEYS = ["sprayProblemComments"];

/**
 * Per-gym prefs live on the member doc (`gyms/{gymId}/members/{uid}.notifPush`)
 * instead of the global user doc — this gives per-gym granularity (route alerts
 * at gym A but not gym B) and lets the CF fan-out query only opted-in members.
 * Everything else is a global pref on `users/{uid}.notificationPrefs`.
 */
export const GYM_SCOPED_KEYS = new Set(["gymNewRoutes", "gymNewComp", "gymNewMember", "gymRouteFreshness", "mauBand", "watchedRoute", "sprayReports"]);

/**
 * `watchedRoute` is the one gym-scoped pref that defaults ON (delivers unless
 * explicitly muted, `!== false`): watching a route is itself an explicit opt-in
 * (the RouteModal bell), so the push should just work without a second toggle.
 * All other gym-scoped prefs are passive broadcasts → opt-in (default off).
 */
export const DEFAULT_ON_GYM_SCOPED_KEYS = new Set(["watchedRoute"]);

/**
 * Seeded ON the first time a user enables push (only keys still unset, so an
 * explicit opt-out is never overwritten). Personal, high-value, low-noise —
 * gym broadcasts and admin alerts stay opt-in (default off).
 */
export const DEFAULT_ON_PERSONAL_KEYS = ["friendRequests", "eventReminder", "eventWaitlist", ...SPRAY_PERSONAL_PREF_KEYS];

/**
 * When push notifications shipped to every user (v0.23.0). Anyone whose account
 * was created after this moment has never known the app without the feature.
 */
export const PUSH_ROLLOUT_MS = Date.UTC(2026, 5, 18); // 2026-06-18

/**
 * Is the one-time push-discovery nudge still owed to this user? Drives the
 * whole badge chain (profile chip → Profilom menu row → Settings tab dot →
 * the Notifications row), so all four call sites must ask the same question.
 *
 * Two conditions, both required:
 *  - `featureSeen.pushNotif` not yet stamped (the section was never opened), and
 *  - the account predates the rollout.
 *
 * The second condition is the point: a "new feature" badge only means something
 * to someone who saw the app WITHOUT the feature. To everyone who registered
 * later it is an unexplained red dot pointing at an ordinary settings row —
 * they go hunting for something new and find nothing. An account age we cannot
 * establish (no `createdAt`, or a `serverTimestamp()` still pending locally on
 * a freshly created doc, both of which `toMillis` reports as 0) counts as NOT
 * eligible: a nudge we cannot justify is exactly what this gate exists to stop.
 */
export function isPushDiscoveryPending(userData) {
  if (!userData || userData.featureSeen?.pushNotif) return false;
  const createdMs = toMillis(userData.createdAt);
  return createdMs > 0 && createdMs < PUSH_ROLLOUT_MS;
}

/** Global (non gym-scoped) keys of a group, in declaration order. */
export function globalKeysOfGroup(group) {
  const g = NOTIF_PREF_GROUPS.find((x) => x.group === group);
  return g ? g.keys.filter((k) => !GYM_SCOPED_KEYS.has(k)) : [];
}

/** Gym-scoped keys of a group, in declaration order. */
export function gymKeysOfGroup(group) {
  const g = NOTIF_PREF_GROUPS.find((x) => x.group === group);
  return g ? g.keys.filter((k) => GYM_SCOPED_KEYS.has(k)) : [];
}

/** Resolve a gym-scoped pref value from a member doc's `notifPush` map. */
export function gymPrefValue(notifPush, key) {
  if (DEFAULT_ON_GYM_SCOPED_KEYS.has(key)) return notifPush?.[key] !== false;
  return notifPush?.[key] === true;
}

/**
 * Fold a just-saved gym-scoped pref into a `{ [gymId]: memberDoc }` map,
 * returning a new map (or the original when there is nothing to patch).
 *
 * The my-gyms hub fetches those member docs ONCE per settings-tab lifetime and
 * is unmounted on every section switch, so a saved value that never made it
 * back into the map got re-seeded from the pre-save snapshot on the next
 * visit — the toggles looked like they had silently reverted even though the
 * write had succeeded.
 */
export function applyNotifPushPatch(memberDocs, gymId, key, value) {
  const md = memberDocs && memberDocs[gymId];
  if (!md) return memberDocs; // no membership cached — nothing to keep in sync
  return {
    ...memberDocs,
    [gymId]: { ...md, notifPush: { ...(md.notifPush || {}), [key]: value } },
  };
}
